import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { AttributesService } from '@emi/shared/data-access';
import * as attributeActions from './attribute-actions';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { AttributeState } from './attribute-reducer';
import { getAttributeState } from './attribute-selectors';
import { AttributeDto, Filters } from '@emi/shared/models';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { createFilterDto } from '@emi/shared/utils';
import * as dataExtractionActions from '../extraction/extraction-actions';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';

@Injectable()
export class AttributeEffects {
    private currentState: AttributeState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private attributesService: AttributesService,
        private attributeStore: Store<AttributeState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.attributeStore.pipe(select(getAttributeState)).subscribe((attributeState) => (this.currentState = attributeState));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeActions.getAttributes),
            map(() => {
                return attributeActions.isLoadingAttributes();
            })
        )
    );

    getAttributesNextPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeActions.getNextPageAttributes),
            switchMap(() => {
                return this.attributesService
                    .getAttributes(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                            searchText:  this.currentState.searchText ?? undefined
                        })
                    )
                    .pipe(
                        map((data: AttributeDto) => {
                            this.previousFilters = this.currentFilters;
                            const newPage = data.results;

                            return attributeActions.updateAttributesSelectionSuccess({
                              attributes: newPage,
                              metadata: data.metadata
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(attributeActions.getAttributesFailure({ error }));
                        })
                    );
            })
        )
    );

    amendSelected$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeActions.amendAttributeSelections),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));

                return this.attributesService
                    .getAttributes(
                        createFilterDto({
                            filterState: this.currentFilters,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                            pageId: this.currentState.metadata?.nextPageId,
                        })
                    )
                    .pipe(
                        map((attributeResponse) => {
                            const amendedAttributes = this.currentFilters.attributes.filter(
                                (a) => !!attributeResponse.results.find((attribute) => attribute.id == a.id)
                            );
                            return dataExtractionActions.addAttributeSelection({ attributes: amendedAttributes });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.status == 404) {
                                return of(dataExtractionActions.addAttributeSelection({ attributes: [] }));
                            }
                            return of(attributeActions.getAttributesFailure({ error }));
                        })
                    );
            })
        )
    );

    fetchAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeActions.fetchAttributes),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.attributes.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            attributeActions.getAttributesSuccess({
                                attributes: this.currentState.attributes,
                                metadata: this.currentState.metadata,
                                searchText: this.currentState.searchText ?? '',
                            })
                        );
                    }
                }
                return this.attributesService
                .getAttributes(
                    createFilterDto({
                      filterState: this.currentFilters,
                      searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined
                }))
                .pipe(
                        map((data: AttributeDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return attributeActions.getAttributesSuccess({
                                attributes: data.results,
                                metadata: data.metadata,
                                searchText: this.currentState.searchText ?? '',
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(attributeActions.getAttributesFailure({ error }));
                        })
                    );
            })
        )
    );

    searchAttributes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeActions.searchAttributes),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            concatLatestFrom(() => this.store.select(getSelectedFilters)),
            switchMap(([action, currentFilters]) => {
                return this.attributesService
                    .getAttributes(
                        createFilterDto({
                            filterState: currentFilters,
                            searchText: action.searchText,
                            searchTerm: this.includeSearchPhrase ? this.searchTermState : undefined,
                        })
                    )
                    .pipe(
                        map((data: AttributeDto) => {
                            this.previousFilters = currentFilters;
                            return attributeActions.getAttributesSuccess({
                                attributes: data.results,
                                metadata: data.metadata,
                                searchText: action.searchText,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(attributeActions.getAttributesFailure({ error }));
                        })
                    );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(attributeActions.isLoadingAttributes),
            map(() => attributeActions.fetchAttributes())
        )
    );
}
